import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { PageHeaderService } from '@ui/app-layout/page-header.service';
import { CardComponent } from '@ui/card/card.component';
import { TableColumn, TableComponent } from '@ui/table/table.component';
import { ButtonComponent } from '@ui/button/button.component';
import { InputComponent } from '@ui/input/input.component';
import { EmployeeService } from '@api/services/employee.service';
import { User } from '@api/models/user.model';
import { UsersModalComponent } from '@features/settings/users/users-modal/users-modal.component';
import { ModalService } from '@ui/modal/modal.service';
import { ConfirmService } from '@ui/confirm-modal/confirm.service';
import { NotificationService } from '@ui/notification/notification.service';
import { EmployeesModalComponent } from './employee-modal/employee-modal.component';
import { AppService } from '../../app.service';
import { Router } from '@angular/router';
import { IndexResponse } from '@api/models/response.model';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-employees',
  standalone: true,
  imports: [CardComponent, TableComponent, ButtonComponent, InputComponent, FormsModule],
  template: `<div class="flex flex-col gap-4 h-[calc(100vh-theme(spacing.24))] overflow-hidden">

  <ui-card>
  <div class="flex justify-end items-center">
      <ui-button [icon]="'add'" (onClick)="addEmployee()" [disabled]="!municipalityId()">Medewerker uitnodigen</ui-button>
    </div>
    <div class="flex flex-col gap-4 justify-between">
      <form (ngSubmit)="applySearch()">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ui-input
            label="Zoeken"
            placeholder="Zoek medewerkers..."
            [(ngModel)]="searchQuery"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onSearchChange()"
            autocomplete="off"
            type="text"
          ></ui-input>

          <div class="flex gap-2 items-end" style="padding-bottom: 2px;">
            <ui-button variant="primary" [icon]="'filter'" type="submit"
              >Filter toepassen</ui-button>
            <ui-button
              variant="ghost"
              [icon]="'refresh'"
              type="button"
              (onClick)="resetSearch()"
              >Reset filters</ui-button>
          </div>
        </div>
      </form>
    </div>
  </ui-card>

  <div class="h-full overflow-hidden">
    <ui-table
      [columns]="columns"
      [data]="data()"
      [loading]="loading()"
      (onRowClick)="onRowClick($event)"
      (onPageChange)="onPageChange($event)"
      [showPagination]="true"
      [meta]="meta()"
      [bordered]="false"
    ></ui-table>
  </div>
</div>`,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmployeesComponent {
  employeeService = inject(EmployeeService);
  modalService = inject(ModalService);
  confirmService = inject(ConfirmService);
  notificationService = inject(NotificationService);
  appService = inject(AppService);
  router = inject(Router);
  municipalityId = this.appService.municipalityId;

  searchQuery = '';

  columns: TableColumn[] = [
    {
      key: 'name',
      label: 'Naam',
    },
    {
      key: 'email',
      label: 'Email',
    },
    {
      key: 'is_consulent',
      label: 'Consulent',
      format: (value: boolean) => (value ? 'Ja' : 'Nee'),
    },
    {
      key: 'is_key_user',
      label: 'Key user',
      format: (value: boolean) => (value ? 'Ja' : 'Nee'),
    },
    {
      key: 'verified_at',
      label: 'Geverifieerd',
      format: (value: string) => (value ? new Date(value).toLocaleDateString('nl-NL') : 'Nee'),
    },
    {
      key: 'last_login_at',
      label: 'Laatste login',
      format: (value: string | null) => value ? new Date(value).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : 'Nog niet ingelogd',
    },
    {
      key: 'created_at',
      label: 'Aangemaakt',
      format: (value: string) => new Date(value).toLocaleDateString('nl-NL'),
    },
    {
      key: 'actions',
      label: '',
      width: '200px',
      actions: [
        {
          label: 'Bewerken',
          icon: 'edit',
          variant: 'primary',
          onClick: (row) => this.editEmployee(row),
        },
        {
          label: 'Verwijderen',
          icon: 'delete',
          variant: 'danger',
          onClick: (row) => this.deleteEmployee(row),
        },
      ],
    },
  ];

  data = signal<User[]>([]);
  loading = signal(false);
  meta = signal<IndexResponse<User>['meta'] | null>(null);

  constructor() {
    effect(() => {
      if (this.appService.municipalityId() && (this.appService.isKeyUser() || this.appService.isAdmin())) {
        this.getEmployees();
      } else {
        this.router.navigate(['/']);
      }
    }, { allowSignalWrites: true });
  }

  getEmployees(page: number = 1, perPage: number = 50, q: string = '', filters: any = {}) {
    this.loading.set(true);
    try {
      this.employeeService.getEmployees(page, perPage, q, filters).subscribe((response) => {
        this.data.set(response.data);
        this.meta.set(response.meta);
        this.loading.set(false);
      });
    } catch (error: any) {
      this.notificationService.error(error.message);
      this.loading.set(false);
    }
  }

  onPageChange(page: number) {
    this.getEmployees(page, 50, this.searchQuery, {});
  }

  onSearchChange() {}

  applySearch() {
    this.getEmployees(1, 50, this.searchQuery, {});
  }

  resetSearch() {
    this.searchQuery = '';
    this.getEmployees(1, 50, '', {});
  }

  addEmployee() {
    let modalRef = this.modalService.open(EmployeesModalComponent);

    modalRef.onClose.subscribe(() => {
      this.getEmployees();
    });
  }

  onRowClick(event: any) {
    console.log('onRowClick', event);
  }

  editEmployee(employee: User) {
    let modalRef = this.modalService.open(EmployeesModalComponent, {
      data: {
        employee: employee,
      },
    });

    modalRef.onClose.subscribe(() => {
      this.getEmployees();
    });
  }

  deleteEmployee(employee: User) {
    this.confirmService
      .confirm({
        title: 'Weet je zeker?',
        message: 'Weet je zeker dat je deze medewerker wilt verwijderen?',
      })
      .then((confirmed: boolean) => {
        if (confirmed) {
          this.employeeService.deleteEmployee(employee.id).subscribe({
            next: () => {
              this.getEmployees();
              this.notificationService.success('Medewerker verwijderd');
            },
            error: (error) => {
              this.notificationService.error(
                'Er is iets fout gegaan bij het verwijderen van de medewerker'
              );
            },
          });
        }
      });
  }

  resendInvite(employee: User) {
    if (employee?.id) {
      return;
    }

    this.employeeService.resendInvite(employee.id).subscribe();
  }
}
